import { useEffect, useMemo, useRef } from "react";
import type { CustomerSupportTicketDetail } from "../../read-models/support/customer-support-read-model";
import { EmptyState, NaveIcon, Notice, Panel } from "../../ui/NavePage";
import { useI18n } from "../i18n/I18nProvider";
import { SupportCompose } from "./SupportCompose";

type Message = NonNullable<CustomerSupportTicketDetail["messages"]>[number];
type Block = Readonly<{ day: string; messages: readonly (Message & { showHead: boolean })[] }>;

const GROUP_WINDOW_MS = 5 * 60_000;
const dayKey = (iso: string): string => {
  const date = new Date(iso);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

/** Messages grouped by day; a sender's consecutive messages within a few minutes share one header. */
const groupMessages = (messages: readonly Message[]): readonly Block[] => {
  const blocks: { day: string; messages: (Message & { showHead: boolean })[] }[] = [];
  let previous: Message | undefined;
  for (const message of messages) {
    const key = dayKey(message.createdAt);
    let block = blocks[blocks.length - 1];
    if (!block || block.day !== key) { block = { day: key, messages: [] }; blocks.push(block); previous = undefined; }
    const sameRun = previous !== undefined && previous.sender === message.sender
      && Date.parse(message.createdAt) - Date.parse(previous.createdAt) < GROUP_WINDOW_MS;
    block.messages.push({ ...message, showHead: !sameRun });
    previous = message;
  }
  return blocks;
};

/**
 * The ticket conversation: a chat-like thread (team on the left, the customer on the right), who the
 * ticket is waiting for, and the reply box right under the last message so nobody has to leave the page.
 */
export const SupportConversation = ({ ticket, ticketId, onSaved }: { ticket: CustomerSupportTicketDetail; ticketId: string; onSaved(): void }) => {
  const { t, locale } = useI18n();
  const scroller = useRef<HTMLDivElement>(null);
  const messages = ticket.messages ?? [];
  const blocks = useMemo(() => groupMessages(messages), [messages]);
  const lastId = messages[messages.length - 1]?.id;

  const time = useMemo(() => new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }), [locale]);
  const dayLabel = (iso: string): string => {
    const date = new Date(iso);
    const today = new Date();
    const diff = Math.round((Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) - Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) / 86_400_000);
    if (diff === 0 || diff === 1) return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(-diff, "day");
    return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }) }).format(date);
  };

  // Open on the latest message, and follow new ones.
  useEffect(() => {
    const element = scroller.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [lastId]);

  const closed = ticket.customerStatus === "RESOLVED" || ticket.customerStatus === "CLOSED";
  const banner = closed ? "support.conversation.closed" : ticket.customerStatus === "WAITING_CLIENT" ? "support.conversation.waitingClient" : "support.conversation.waitingCupcode";

  return (
    <Panel id="support-conversation-heading" icon="lucide:messages-square" title={t("support.conversation")} {...(messages.length ? { count: messages.length } : {})}>
      <Notice tone={ticket.customerStatus === "WAITING_CLIENT" ? "warning" : "info"} icon={closed ? "lucide:check-circle-2" : ticket.customerStatus === "WAITING_CLIENT" ? "lucide:message-circle-more" : "lucide:clock"}>{t(banner)}</Notice>
      {messages.length ? (
        <div className="nave-thread-scroll" ref={scroller} tabIndex={0} role="log" aria-live="polite" aria-labelledby="support-conversation-heading">
          {ticket.hasMoreMessages ? <Notice>{t("support.moreMessages")}</Notice> : null}
          {blocks.map((block) => (
            <section key={block.day} className="nave-thread-day">
              <h3 className="nave-thread-date"><span>{dayLabel(block.messages[0]?.createdAt ?? "")}</span></h3>
              <ol className="nave-thread">
                {block.messages.map((message) => (
                  <li key={message.id} className={`nave-message${message.sender === "CLIENT" ? " nave-message-client" : ""}${message.showHead ? "" : " nave-message-follow"}`}>
                    {message.sender === "CUPCODE" ? <span className="nave-message-avatar" aria-hidden="true">{message.showHead ? <NaveIcon name="lucide:headset" /> : null}</span> : null}
                    <div className="nave-message-body">
                      {message.showHead ? <span className="nave-message-head"><strong>{t(message.sender === "CLIENT" ? "support.sender.you" : "support.sender.CUPCODE")}</strong></span> : null}
                      <p className="nave-message-bubble">{message.text}</p>
                      <time className="nave-message-time" dateTime={message.createdAt}>{time.format(new Date(message.createdAt))}</time>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      ) : <EmptyState icon="lucide:message-circle">{t("support.noMessages")}</EmptyState>}
      <SupportCompose key={ticketId} ticketId={ticketId} bare onSaved={onSaved} />
    </Panel>
  );
};
